import { useState } from "react";
import { toast } from "sonner";
import { Loader2, FileText, CreditCard, CheckCircle2, Download } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PageHero } from "./Services";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function PayInvoice() {
  const [form, setForm] = useState({ invoiceNumber: "", email: "" });
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    setLoading(true); setInvoice(null);
    try { const res = await api.post("/invoices/lookup", { invoiceNumber: form.invoiceNumber, email: form.email }); setInvoice(res.data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const pay = async () => {
    setPaying(true);
    try {
      const res = await api.post("/payments/invoice/checkout", { invoiceId: invoice.id, email: form.email, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setPaying(false); }
  };

  const input = "w-full h-11 px-3 rounded-lg border border-input bg-white focus:outline-none focus:ring-2 focus:ring-electric text-sm";
  return (
    <div>
      <PageHero label="Pay Invoice" title="Pay your invoice securely" subtitle="Enter your invoice number and email to view and pay. Payments are processed securely by Stripe." />
      <section className="max-w-xl mx-auto px-4 sm:px-6 py-14">
        {!invoice ? (
          <form onSubmit={lookup} data-testid="invoice-lookup-form" className="space-y-4 rounded-2xl border border-slate-200 p-6">
            <label className="block"><span className="block text-sm font-medium text-navy mb-1.5">Invoice number</span><input required className={input} placeholder="INV-XXXXXXXX" value={form.invoiceNumber} onChange={e => setForm({ ...form, invoiceNumber: e.target.value })} data-testid="lookup-number" /></label>
            <label className="block"><span className="block text-sm font-medium text-navy mb-1.5">Email on invoice</span><input required type="email" className={input} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="lookup-email" /></label>
            <Button type="submit" disabled={loading} className="w-full bg-electric hover:bg-tech" data-testid="lookup-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Find Invoice"}</Button>
          </form>
        ) : (
          <div className="rounded-2xl border border-slate-200 overflow-hidden" data-testid="invoice-detail">
            <div className="tdw-navy-bg text-white p-6 flex justify-between items-start">
              <div><FileText className="h-6 w-6 text-tech mb-2" /><p className="font-heading font-bold text-lg">{invoice.invoiceNumber}</p><p className="text-sm text-slate-300">{invoice.customerName}</p></div>
              <span className="text-xs px-2.5 py-1 rounded-full bg-white/10 capitalize">{invoice.status.replace("_", " ")}</span>
            </div>
            <div className="p-6">
              <div className="space-y-2">
                {invoice.items.map((it, i) => (
                  <div key={i} className="flex justify-between text-sm"><span className="text-slate-600">{it.name} × {it.quantity}</span><span className="text-navy font-medium">{money(it.lineTotal)}</span></div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t space-y-1.5 text-sm">
                <Row label="Subtotal" value={money(invoice.subtotal)} />
                {invoice.discount > 0 && <Row label="Discount" value={"-" + money(invoice.discount)} />}
                {invoice.tax > 0 && <Row label={`Tax (${invoice.taxRate}%)`} value={money(invoice.tax)} />}
                <Row label="Total" value={money(invoice.total)} bold />
                {invoice.amountPaid > 0 && <Row label="Paid" value={money(invoice.amountPaid)} />}
                <Row label="Balance Due" value={money(invoice.balance)} bold accent />
              </div>
              {invoice.balance > 0 ? (
                <Button onClick={pay} disabled={paying} className="w-full mt-6 bg-electric hover:bg-tech gap-2" data-testid="pay-now-btn">
                  {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4" /> Pay {money(invoice.balance)}</>}
                </Button>
              ) : (
                <div className="mt-6 flex flex-col gap-2">
                  <div className="flex items-center justify-center gap-2 text-green-600 font-medium"><CheckCircle2 className="h-5 w-5" /> Paid in full</div>
                  <a href={`${API}/invoices/${invoice.id}/receipt?email=${encodeURIComponent(form.email)}`} target="_blank" rel="noreferrer">
                    <Button variant="outline" className="w-full gap-2" data-testid="receipt-btn"><Download className="h-4 w-4" /> Download Receipt</Button>
                  </a>
                </div>
              )}
              <button onClick={() => setInvoice(null)} className="w-full mt-3 text-sm text-slate-500 hover:text-electric">Look up a different invoice</button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function Row({ label, value, bold, accent }) {
  return <div className="flex justify-between"><span className={accent ? "text-electric font-semibold" : "text-slate-600"}>{label}</span><span className={`${bold ? "font-bold" : ""} ${accent ? "text-electric" : "text-navy"}`}>{value}</span></div>;
}
